import { useCallback, useEffect, useMemo, useState } from 'react';
import { Library, Plug, PlusCircle, Search } from 'lucide-react';
import PillTabs from '../../../../../components/common/PillTabs';
import { useTheme } from '../../../../../components/common/Theme';
import ConfirmDialog from '../../../Dialogs/ConfirmDialog';
import {
  catalogToFormValues,
  connectionToFormValues,
  emptyFormValues,
  mcpConnectionService,
} from './mcpConnectionService';
import { logMcp } from './mcpLogger';
import type {
  ConnectionFormValues,
  ConnectionStatus,
  McpCatalogEntry,
  McpServerConnection,
  McpTestResult,
} from './types';
import ConnectionsList from './components/ConnectionsList';
import ConnectionForm from './components/ConnectionForm';
import CatalogBrowser from './components/CatalogBrowser';
import ConnectionDetail from './components/ConnectionDetail';
import './mcpResourcesV2.css';

type Tab = 'connections' | 'catalog';

type FormMode =
  | { kind: 'closed' }
  | { kind: 'create'; initial: ConnectionFormValues }
  | { kind: 'edit'; id: string; initial: ConnectionFormValues };

export default function MCPResourcesV2() {
  const { theme } = useTheme();
  const isLight = theme === 'white';
  const [connections, setConnections] = useState<McpServerConnection[]>([]);
  const [catalog, setCatalog] = useState<McpCatalogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>('connections');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<ConnectionStatus | 'all'>('all');
  const [catalogSearch, setCatalogSearch] = useState('');
  const [catalogTag, setCatalogTag] = useState<string>('all');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [formMode, setFormMode] = useState<FormMode>({ kind: 'closed' });
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [busyKind, setBusyKind] = useState<'test' | 'refresh' | null>(null);

  const selected = useMemo(
    () => connections.find((c) => c.id === selectedId) ?? null,
    [connections, selectedId]
  );

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [nextConnections, nextCatalog] = await Promise.all([
        mcpConnectionService.list(),
        mcpConnectionService.listCatalog(),
      ]);
      setConnections(nextConnections);
      setCatalog(nextCatalog);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load MCP connections';
      setError(message);
      logMcp('error', 'page_load_failed', { message });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadAll();
  }, [loadAll]);

  const replaceConnection = (next: McpServerConnection) => {
    setConnections((prev) => {
      const idx = prev.findIndex((c) => c.id === next.id);
      if (idx < 0) return [next, ...prev];
      const copy = [...prev];
      copy[idx] = next;
      return copy;
    });
  };

  const filteredConnections = useMemo(() => {
    const q = search.trim().toLowerCase();
    return connections.filter((c) => {
      if (statusFilter !== 'all' && c.status !== statusFilter) return false;
      if (!q) return true;
      return [c.name, c.description ?? '', c.code ?? '', c.transport, c.source, ...c.tags]
        .join(' ')
        .toLowerCase()
        .includes(q);
    });
  }, [connections, search, statusFilter]);

  const catalogTags = useMemo(() => {
    const set = new Set<string>();
    for (const e of catalog) for (const t of e.tags) set.add(t);
    return Array.from(set).sort();
  }, [catalog]);

  const filteredCatalog = useMemo(() => {
    const q = catalogSearch.trim().toLowerCase();
    return catalog.filter((e) => {
      if (catalogTag !== 'all' && !e.tags.includes(catalogTag)) return false;
      if (!q) return true;
      return [e.name, e.code, e.description, e.publisher, ...e.tags].join(' ').toLowerCase().includes(q);
    });
  }, [catalog, catalogSearch, catalogTag]);

  const openCreate = () => {
    setFormMode({ kind: 'create', initial: emptyFormValues() });
  };

  const openEdit = (id: string) => {
    const conn = connections.find((c) => c.id === id);
    if (!conn) return;
    setFormMode({ kind: 'edit', id, initial: connectionToFormValues(conn) });
  };

  const openCatalogConfigure = (entry: McpCatalogEntry) => {
    setFormMode({ kind: 'create', initial: catalogToFormValues(entry) });
  };

  const handleSave = async (values: ConnectionFormValues) => {
    if (formMode.kind === 'edit') {
      const updated = await mcpConnectionService.update(formMode.id, values);
      replaceConnection(updated);
      setFormMode({ kind: 'closed' });
      return;
    }
    if (formMode.kind === 'create') {
      const discover = values.source === 'catalog';
      logMcp('app', 'create_save', { name: values.name, source: values.source, discover });
      const created = await mcpConnectionService.create(values, discover);
      replaceConnection(created);
      setSelectedId(created.id);
      setTab('connections');
      setFormMode({ kind: 'closed' });
    }
  };

  const handleSaveAndTest = async (values: ConnectionFormValues) => {
    if (formMode.kind === 'edit') {
      const id = formMode.id;
      await mcpConnectionService.update(id, values);
      setFormMode({ kind: 'closed' });
      setSelectedId(id);
      setBusyId(id);
      try {
        const result = await mcpConnectionService.test(id);
        if (result.connection) replaceConnection(result.connection);
        else replaceConnection(await mcpConnectionService.get(id));
      } finally {
        setBusyId(null);
      }
      return;
    }
    if (formMode.kind === 'create') {
      const created = await mcpConnectionService.create(values, true);
      replaceConnection(created);
      if (created.status === 'failed') {
        const message = created.lastError || 'Connection test failed';
        logMcp('warning', 'create_save_and_test_failed', { id: created.id, message });
        throw new Error(message);
      }
      setFormMode({ kind: 'closed' });
      setTab('connections');
      setSelectedId(created.id);
    }
  };

  const handleTestDraft = async (values: ConnectionFormValues): Promise<McpTestResult> => {
    return mcpConnectionService.testDraft(values);
  };

  const handleDiscover = async (id: string, kind: 'test' | 'refresh') => {
    setBusyId(id);
    setBusyKind(kind);
    setConnections((prev) => prev.map((c) => (c.id === id ? { ...c, status: 'testing' as const } : c)));
    try {
      const result =
        kind === 'refresh' ? await mcpConnectionService.refreshTools(id) : await mcpConnectionService.test(id);
      if (result.connection) {
        replaceConnection(result.connection);
      } else {
        replaceConnection(await mcpConnectionService.get(id));
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : kind === 'refresh' ? 'Failed to update tools' : 'Test failed';
      logMcp('error', kind === 'refresh' ? 'refresh_tools_page_failed' : 'test_connection_page_failed', {
        id,
        message,
      });
      setConnections((prev) =>
        prev.map((c) => (c.id === id ? { ...c, status: 'failed', lastError: message } : c))
      );
      throw err;
    } finally {
      setBusyId(null);
      setBusyKind(null);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteId) return;
    const id = deleteId;
    setDeleteId(null);
    await mcpConnectionService.remove(id);
    setConnections((prev) => prev.filter((c) => c.id !== id));
    if (selectedId === id) setSelectedId(null);
  };

  const handleToggleActive = async (id: string) => {
    const conn = connections.find((c) => c.id === id);
    if (!conn) return;
    const values = connectionToFormValues(conn);
    values.isActive = !conn.isActive;
    const updated = await mcpConnectionService.update(id, values);
    replaceConnection(updated);
  };

  const form = (
    <ConnectionForm
      open={formMode.kind !== 'closed'}
      title={
        formMode.kind === 'edit'
          ? 'Edit connection'
          : formMode.kind === 'create' && formMode.initial.source === 'catalog'
            ? 'Configure catalog server'
            : 'Add custom connection'
      }
      initial={formMode.kind === 'closed' ? emptyFormValues() : formMode.initial}
      onClose={() => setFormMode({ kind: 'closed' })}
      onSave={handleSave}
      onSaveAndTest={handleSaveAndTest}
      onTestDraft={handleTestDraft}
    />
  );

  if (selected) {
    return (
      <div className="mcp-v2">
        <ConnectionDetail
          connection={selected}
          testing={busyId === selected.id && busyKind === 'test'}
          refreshing={busyId === selected.id && busyKind === 'refresh'}
          onBack={() => setSelectedId(null)}
          onTest={() => handleDiscover(selected.id, 'test')}
          onRefreshTools={() => handleDiscover(selected.id, 'refresh')}
          onEdit={() => openEdit(selected.id)}
          onSaveSelections={async (toolEnabled, resourceEnabled) => {
            const updated = await mcpConnectionService.saveSelections(
              selected.id,
              toolEnabled,
              resourceEnabled
            );
            replaceConnection(updated);
          }}
          onInvokeTool={(toolId, toolName, args) =>
            mcpConnectionService.invokeTool(selected.id, toolId, toolName, args)
          }
        />
        {form}
      </div>
    );
  }

  return (
    <div className="mcp-v2">
      <div className="mcp-v2__header">
        <p className="mcp-v2__eyebrow">AI Gateway Fabric</p>
        <h1 className="mcp-v2__title">
          External MCPs
          <span className="mcp-v2__subtitle">
            Register an MCP server once, test the handshake, and keep every discovered tool on that
            connection.
          </span>
        </h1>
      </div>

      {error ? (
        <div className="mcp-v2__alert mcp-v2__alert--error">
          {error}
          <button type="button" className="mcp-v2__btn mcp-v2__btn--ghost" onClick={() => void loadAll()}>
            Retry
          </button>
        </div>
      ) : null}

      <div className="mcp-v2__tabs-row">
        <PillTabs
          isLight={isLight}
          ariaLabel="External MCP sections"
          value={tab}
          items={[
            { id: 'connections', label: 'My connections', icon: Plug, count: connections.length },
            { id: 'catalog', label: 'Browse catalog', icon: Library, count: catalog.length },
          ]}
          onChange={setTab}
        />
        <button type="button" className="mcp-v2__btn mcp-v2__btn--primary mcp-v2__btn--pill" onClick={openCreate}>
          <PlusCircle className="w-4 h-4" aria-hidden />
          Add custom connection
        </button>
      </div>

      {loading ? (
        <div className="mcp-v2__panel">
          <div className="mcp-v2__empty">
            <h3>Loading connections…</h3>
            <p>Fetching registered MCP servers and catalog templates.</p>
          </div>
        </div>
      ) : tab === 'connections' ? (
        <>
          <div className="mcp-v2__toolbar">
            <div className="mcp-v2__search">
              <Search aria-hidden />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search connections…"
                aria-label="Search connections"
              />
            </div>
            <select
              className="mcp-v2__select"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as ConnectionStatus | 'all')}
              aria-label="Filter by status"
            >
              <option value="all">All statuses</option>
              <option value="connected">Connected</option>
              <option value="never_tested">Not tested</option>
              <option value="failed">Failed</option>
              <option value="testing">Testing</option>
            </select>
          </div>
          <ConnectionsList
            connections={filteredConnections}
            onOpen={setSelectedId}
            onEdit={openEdit}
            onDelete={setDeleteId}
            onToggleActive={(id) => void handleToggleActive(id)}
          />
        </>
      ) : (
        <>
          <div className="mcp-v2__toolbar">
            <div className="mcp-v2__search">
              <Search aria-hidden />
              <input
                value={catalogSearch}
                onChange={(e) => setCatalogSearch(e.target.value)}
                placeholder="Search MCP repositories…"
                aria-label="Search catalog"
              />
            </div>
            <select
              className="mcp-v2__select"
              value={catalogTag}
              onChange={(e) => setCatalogTag(e.target.value)}
              aria-label="Filter by tag"
            >
              <option value="all">All tags</option>
              {catalogTags.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </div>
          <CatalogBrowser entries={filteredCatalog} onConfigure={openCatalogConfigure} />
        </>
      )}

      {form}

      {deleteId ? (
        <ConfirmDialog
          title="Delete MCP connection?"
          message="This removes the connection and its stored tools from the server. This cannot be undone."
          onYes={() => void handleConfirmDelete()}
          onCancel={() => setDeleteId(null)}
        />
      ) : null}
    </div>
  );
}
